import { useState, useEffect, useCallback, useContext, useRef, Fragment } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthProvider';
import { toast } from 'react-toastify';
import '../styles/proposalPrint.css';
import { moment } from '../utils/dateUtils';
import { 
  Box, Typography, CircularProgress, Button, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, TextField, FormControlLabel, Checkbox
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import { calculateTotalPay } from '../utils/proposalCalculations';
import proposalsServices from '../services/proposalsServices';
import integrationServices from '../services/integrationServices';
import printServices from '../services/printServices';

import { stripHtml } from '../utils/htmlUtils';

const MAX_PROPOSALS = 50;
const LAST_PRINTER_KEY = 'serverPrintLastPrinter';

// Usable page area (paper minus 10mm margins on every side), in CSS px.
// The browser flow keeps the historical A4 numbers (duplex parity was
// validated against real Chrome output for A4 — do not change them).
// Server-side rendering passes ?pageSize=letter because the office
// printers are loaded with US Letter: the PDF is rendered as Letter and
// CUPS prints media=letter, so the measurement must use Letter heights,
// otherwise the odd-page (duplex blank) parity drifts.
const PAGE_AREAS = {
  a4:     { width: 190,          height: 277 },          // 210x297 - 20mm
  letter: { width: 215.9 - 20,   height: 279.4 - 20 },   // 8.5x11in - 20mm
};

const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1'))
    return `${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  if (cleaned.length === 10)
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  return phone;
};

const PrintBatch = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  // Server-side printing is admin-only (the API enforces this too).
  const canPrintOnServer = ['admin', 'superadmin'].includes(userData?.role);
  
  const [proposals, setProposals] = useState([]);
  const [warehouseData, setWarehouseData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [oddPageIds, setOddPageIds] = useState(new Set());
  const [regenId, setRegenId] = useState(null);
  const [hideSections, setHideSections] = useState([]);
  const proposalRefs = useRef({});

  // Server-side printing (CUPS printers attached to the server)
  const [serverPrintOpen, setServerPrintOpen] = useState(false);
  const [serverPrinters, setServerPrinters] = useState([]);
  const [serverPrintersLoading, setServerPrintersLoading] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [copies, setCopies] = useState(1);
  const [duplex, setDuplex] = useState(true);
  const [serverPrinting, setServerPrinting] = useState(false);
  // Which contracts to print (defaults to all every time the dialog opens).
  const [printIds, setPrintIds] = useState(new Set());

  const ids = searchParams.get('ids')?.split(',').filter(Boolean) || [];
  const installDateFilter = searchParams.get('installDate') || null;
  // ?pageSize=... is only ever set by the API's headless-Chrome renderer
  // (server-side printing). The server renders every proposal as a separate
  // one-id PDF and handles duplex parity itself while merging (exact page
  // counts come from the PDFs), so the JS blank-page measurement is skipped.
  // The measurement below stays for the in-browser print flow only.
  const pageSize = (searchParams.get('pageSize') || '').toLowerCase();
  const serverRender = pageSize !== '';
  const pageArea = PAGE_AREAS[pageSize] || PAGE_AREAS.a4;
  const printWidthPx = pageArea.width * 96 / 25.4;
  const printHeightPx = pageArea.height * 96 / 25.4;

  const loadProposals = useCallback(async () => {
    if (ids.length === 0) { setLoading(false); return; }
    if (ids.length > MAX_PROPOSALS) {
      setError(`Maximum ${MAX_PROPOSALS} proposals at once. You selected ${ids.length}.`);
      setLoading(false);
      return;
    }

    try {
      const proposalsResponse = await proposalsServices.getBatch(ids);

      const proposalsMap = {};
      proposalsResponse.data.forEach(p => { proposalsMap[p.id] = p; });
      const sortedProposals = ids.map(id => proposalsMap[id]).filter(Boolean);
      setProposals(sortedProposals);
      
      const warehouseDataMap = {};
      await Promise.all(
        sortedProposals.map(async (proposal) => {
          const warehousePO = proposal.warrantyTicket?.ticketNumber || proposal.publicId;
          try {
            const eqResp = await integrationServices.getWarehouseEquipment(warehousePO);
            const rawEquipment = eqResp.data || [];
            const normalizedPo = String(warehousePO || '').trim().toUpperCase();
            const eqWithPo = rawEquipment.filter(eq => eq?.po_number !== undefined && eq?.po_number !== null);
            const equipment = eqWithPo.length
              ? eqWithPo.filter(eq => String(eq.po_number).trim().toUpperCase() === normalizedPo)
              : rawEquipment;
            
            let supplies = [];
            try {
              const recResp = await integrationServices.getRecognitionData(warehousePO);
              if (recResp.data?.ok) {
                const rawSupplies = recResp.data.supplies || [];
                const supWithPo = rawSupplies.filter(s => s?.po_number !== undefined && s?.po_number !== null);
                supplies = supWithPo.length
                  ? supWithPo.filter(s => String(s.po_number).trim().toUpperCase() === normalizedPo)
                  : rawSupplies;
              }
            } catch (e) {
              console.warn(`Recognition data for ${warehousePO}:`, e.message);
            }
            
            if (equipment.length > 0 || supplies.length > 0) {
              warehouseDataMap[proposal.id] = { equipment, supplies };
            }
          } catch (err) {
            console.warn(`Warehouse data for ${warehousePO}:`, err.message);
          }
        })
      );
      setWarehouseData(warehouseDataMap);
      
      if (sortedProposals.length < ids.length) {
        toast.warning(`${ids.length - sortedProposals.length} proposal(s) could not be loaded`);
      }
    } catch (err) {
      console.error('Error loading proposals:', err);
      setError(err.response?.data?.message || 'Failed to load proposals');
    } finally {
      setLoading(false);
    }
  }, [ids]);

  useEffect(() => { loadProposals(); }, []);

  // Which source sections to drop from the foreman document once LLM work
  // instructions exist (configured on the AI settings page).
  useEffect(() => {
    integrationServices.getForemanDocSettings()
      .then(res => setHideSections(res.data?.hideSections || []))
      .catch(() => {});
  }, []);

  // Readiness signals for server-side printing: headless Chrome on the API
  // waits for window.__BATCH_PRINT_READY before rendering the PDF (plain
  // network-idle is not enough — duplex page measurement runs on a timer).
  // The multi-proposal case is flagged at the end of the measurement effect.
  useEffect(() => {
    if (error) { window.__BATCH_PRINT_ERROR = error; return; }
    if (loading) return;
    if (proposals.length === 0) {
      window.__BATCH_PRINT_ERROR = 'No proposals to print';
    } else if (proposals.length === 1) {
      window.__BATCH_PRINT_READY = true;
    }
  }, [loading, error, proposals]);

  // After render, clone each proposal into a print-width container to
  // accurately measure page count and insert blank pages for duplex.
  useEffect(() => {
    if (loading || proposals.length < 2) return;
    if (serverRender) {
      // Server-side rendering handles duplex parity while merging per-id
      // PDFs; no measured blank pages needed (oddPageIds stays empty).
      window.__BATCH_PRINT_READY = true;
      return;
    }
    const timer = setTimeout(() => {
      const odds = new Set();
    proposals.forEach((proposal, index) => {
        if (index === proposals.length - 1) return;
        const el = proposalRefs.current[proposal.id];
        if (!el) return;

        const wrap = document.createElement('div');
        wrap.style.cssText = `position:absolute;left:-99999px;top:-99999px;width:${printWidthPx}px;visibility:hidden;overflow:visible;`;
        const clone = el.cloneNode(true);
        clone.style.cssText = 'padding:0;margin:0;width:100%;max-width:none;box-shadow:none;border:none;border-radius:0;';
        wrap.appendChild(clone);
        document.body.appendChild(wrap);

        const h = clone.scrollHeight;

        // Naive ceil(height / pageHeight) under-counts pages: the print CSS
        // keeps many blocks together (`break-inside: avoid` on table rows,
        // signature / total / notes, and `break-after: avoid` on section
        // titles). When such a block straddles a page boundary the browser
        // pushes the WHOLE block to the next page, adding vertical space the
        // raw scrollHeight never sees. Simulate that so the parity (and the
        // duplex blank page) matches what Chrome actually prints.
        //
        // The page a block lands on is derived directly from its shifted top
        // (no running "pageBottom" accumulator — that earlier approach drifted
        // and produced negative shifts, undercounting tall proposals). `shift`
        // only ever grows, so pushing a block also pushes everything after it,
        // exactly like real document flow. Validated against headless-Chrome
        // PDF output for the June-8 batch (matches real page counts 9/9).
        const base = clone.getBoundingClientRect().top;
        const blocks = Array.from(
          clone.querySelectorAll(
            'tr, .bp-signature, .bp-total, .bp-notes, .bp-customer, .bp-billing, .bp-payment-notes, .bp-pre, .bp-list li, .bp-section-title'
          )
        )
          .map((a) => {
            const r = a.getBoundingClientRect();
            return { top: r.top - base, bottom: r.bottom - base };
          })
          .sort((x, y) => x.top - y.top);

        let shift = 0;
        for (const b of blocks) {
          const top = b.top + shift;
          const bottom = b.bottom + shift;
          const blockH = b.bottom - b.top;
          if (blockH > 0 && blockH <= printHeightPx) {
            const pageEnd = (Math.floor(top / printHeightPx) + 1) * printHeightPx;
            if (bottom > pageEnd) shift += pageEnd - top; // push whole block to next page
          }
        }
        const pages = Math.ceil((h + shift) / printHeightPx);
        if (pages % 2 === 1) odds.add(proposal.id);
        console.log(`[Duplex] ${proposal.publicId}: raw ${h}px +${Math.round(shift)}px push → ${pages} pg, blank=${pages % 2 === 1}`);

        document.body.removeChild(wrap);
      });
      setOddPageIds(odds);
      window.__BATCH_PRINT_READY = true;
      }, 500);
      return () => clearTimeout(timer);
  }, [loading, proposals, hideSections]);

  // --- Data extraction helpers ---

  // When the client accepted one of the proposal options, only the systems
  // belonging to that option are part of the contract — equipment/scope/notes
  // from the declined options must not appear on the foreman document.
  // Same filter as calculateTotalPay / ProposalPublicView.
  const getActiveSystems = (proposal) => {
    const systems = proposal.proposalSystems || [];
    return proposal.optionAccepted && proposal.selectedOptionId
      ? systems.filter(s => s.optionId === proposal.selectedOptionId)
      : systems;
  };

  const getJobOverview = (proposal) => {
    if (proposal.jobOverview) return proposal.jobOverview;
    const parts = getActiveSystems(proposal).filter(s => s.notes).map(s => s.notes);
    if (parts.length) return parts.join('\n\n');
    return null;
  };

  const getEquipment = (proposal) => {
    const whData = warehouseData[proposal.id] || {};
    if (whData.equipment?.length > 0) {
      return whData.equipment.map(eq => ({
        type: eq.equipment_type || '',
        manufacturer: eq.manufacturer || '',
        model: eq.model || '',
        serial: eq.serial_number || ''
      }));
    }
    const rows = [];
    getActiveSystems(proposal).forEach(system => {
      system.equipments?.forEach(eq => {
        rows.push({
          type: eq.equipmentType || '',
          manufacturer: eq.manufacturerName || '',
          model: eq.modelNumber || '',
          serial: ''
        });
      });
    });
    proposal.data?.systems?.forEach(system => {
      if (system.usePromptGeneration) return;
      system.equipments?.forEach(eq => {
        const idx = eq.selectedItemIndex || 0;
        const item = eq.flexibleItems?.[idx]?.item;
        if (!item) return;
        rows.push({
          type: item.type || '',
          manufacturer: item.make || '',
          model: item.model || '',
          serial: ''
        });
      });
    });
    return rows;
  };

  const getSupplies = (proposal) => {
    const whData = warehouseData[proposal.id] || {};
    return (whData.supplies || []).map(s => {
      // Supply objects come from Warehouse `/search/recognition/<PO>` and
      // have shape { model, description, quantity, ... } — there is no
      // `name` field. The previous version did `s.model || s.name ||
      // s.description`, which silently dropped the description (e.g. you
      // saw "DDS-30" instead of "DDS-30 - DISCONNECT FUSE 30A 240V 1P").
      // Glue them back together the same way Warehouse does it in its
      // own text report (invoice_recognition.generate_supplies_report_text).
      const model = (s.model || s.name || '').trim();
      const desc = (s.description || '').trim();
      let label;
      if (model && desc && model.toLowerCase() !== desc.toLowerCase()) {
        label = `${model} - ${desc}`;
      } else {
        label = model || desc || 'Supply';
      }
      return {
        name: label,
        qty: s.quantity || ''
      };
    });
  };

  const getScopeItems = (proposal) => {
    const items = [];
    proposal.scopeOfWorks?.forEach(w => items.push(w.description));
    getActiveSystems(proposal).forEach(s => {
      s.scopeOfWorks?.forEach(w => items.push(w.description));
    });
    proposal.data?.systems?.forEach(s => {
      s.scopeOfWorks?.forEach(w => {
        const idx = w.selectedItemIndex || 0;
        if (w.flexibleItems?.[idx]?.item?.description) {
          items.push(w.flexibleItems[idx].item.description);
        }
      });
    });
    return items.filter(Boolean);
  };

  const getInstallInfo = (proposal) => {
    let schedule = proposal.installationSchedule || [];
    if (installDateFilter && schedule.length) {
      schedule = schedule.filter(s =>
        moment(s.date).utc().format('YYYY-MM-DD') === installDateFilter
      );
    }
    let dayInfo = null;
    if (installDateFilter && proposal.installationSchedule?.length > 1) {
      const sorted = [...proposal.installationSchedule].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
      const di = sorted.findIndex(s =>
        moment(s.date).utc().format('YYYY-MM-DD') === installDateFilter
      );
      if (di >= 0) dayInfo = { day: di + 1, total: sorted.length };
    }
    return { schedule, dayInfo };
  };

  // Regenerate the LLM work instructions for a single proposal and patch it
  // into local state (avoids a full reload of all proposals + warehouse data).
  const handleRegenInstructions = async (proposalId) => {
    setRegenId(proposalId);
    try {
      const res = await proposalsServices.generateForemanInstructions(proposalId);
      const text = res.data?.foremanInstructions || '';
      setProposals(prev => prev.map(p => (p.id === proposalId ? { ...p, foremanInstructions: text } : p)));
      toast.success('Work instructions regenerated');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to generate instructions');
    } finally {
      setRegenId(null);
    }
  };

  // --- Server-side printing ---

  const getClientName = (proposal) => {
    const client = proposal.selectedClientId
      ? proposal.property?.clients?.find(c => c.client?.id === proposal.selectedClientId)?.client
      : proposal.property?.clients?.[0]?.client;
    return client?.fullName || proposal.property?.address || '';
  };

  const togglePrintId = (id) => {
    setPrintIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleOpenServerPrint = async () => {
    setServerPrintOpen(true);
    setPrintIds(new Set(proposals.map(p => p.id)));
    setServerPrintersLoading(true);
    try {
      const res = await printServices.getServerPrinters();
      const printers = res.data?.printers || [];
      setServerPrinters(printers);

      const remembered = localStorage.getItem(LAST_PRINTER_KEY);
      const preselect =
        printers.find(p => p.name === remembered)?.name ||
        res.data?.defaultPrinter ||
        printers[0]?.name || '';
      setSelectedPrinter(preselect);
    } catch (e) {
      setServerPrinters([]);
      toast.error(e.response?.data?.message || 'Failed to load server printers');
    } finally {
      setServerPrintersLoading(false);
    }
  };

  const handleServerPrint = async () => {
    if (!selectedPrinter) return;
    // Keep the on-page order; print only the checked contracts.
    const selectedIds = proposals.filter(p => printIds.has(p.id)).map(p => p.id);
    if (selectedIds.length === 0) return;
    setServerPrinting(true);
    try {
      const res = await printServices.printBatchOnServer({
        ids: selectedIds,
        installDate: installDateFilter || undefined,
        printer: selectedPrinter,
        copies,
        duplex,
      });
      localStorage.setItem(LAST_PRINTER_KEY, selectedPrinter);
      toast.success(`Sent to "${selectedPrinter}"${res.data?.jobId ? ` (job ${res.data.jobId})` : ''}`);
      setServerPrintOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Server print failed');
    } finally {
      setServerPrinting(false);
    }
  };

  // --- Render states ---

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 3, p: 4 }}>
        <WarningIcon sx={{ fontSize: 64, color: '#d32f2f' }} />
        <Typography variant="h5" color="error">Print Error</Typography>
        <Alert severity="error" sx={{ maxWidth: 500 }}>{error}</Alert>
          <Button variant="outlined" onClick={() => navigate(-1)}>Go Back</Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
        <CircularProgress size={48} />
        <Typography>Loading {ids.length} proposals…</Typography>
      </Box>
    );
  }

  if (proposals.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Typography variant="h5">No proposals to print</Typography>
      </Box>
    );
  }

  return (
    <Fragment>
      {/* Screen-only header bar */}
      <div className="no-print bp-screen-header">
        <span>
          <strong>{proposals.length}</strong> proposal{proposals.length !== 1 ? 's' : ''} ready
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          {proposals.length === 1 && (
            <Button size="small" variant="contained" startIcon={<EditIcon />}
              onClick={() => navigate(`/proposals/${proposals[0].id}`)}
              sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}>
              Edit
            </Button>
          )}
          <Button size="small" variant="contained" startIcon={<PrintIcon />}
            onClick={() => window.print()}
            sx={{ bgcolor: '#fff', color: '#1976d2', '&:hover': { bgcolor: '#f5f5f5' } }}>
            Print
          </Button>
          {canPrintOnServer && (
            <Button size="small" variant="contained" startIcon={<LocalPrintshopIcon />}
              onClick={handleOpenServerPrint}
              sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#43a047' } }}>
              Print on Server
            </Button>
          )}
          <Button size="small" variant="outlined"
            onClick={() => { window.close(); navigate(-1); }}
            sx={{ borderColor: '#fff', color: '#fff', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
            Close
          </Button>
        </div>
      </div>

      {/* Server-side print dialog (hidden in print CSS) */}
      <Dialog open={serverPrintOpen} onClose={() => !serverPrinting && setServerPrintOpen(false)} maxWidth="xs" fullWidth className="no-print">
        <DialogTitle>Print on Server</DialogTitle>
        <DialogContent>
          {serverPrintersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : serverPrinters.length === 0 ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              No printers are configured on the server.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="server-printer-label">Printer</InputLabel>
                <Select
                  labelId="server-printer-label"
                  label="Printer"
                  value={selectedPrinter}
                  onChange={(e) => setSelectedPrinter(e.target.value)}
                >
                  {serverPrinters.map((p) => (
                    <MenuItem key={p.name} value={p.name}>
                      {p.name}{p.isDefault ? ' (default)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Copies" type="number" size="small"
                value={copies}
                onChange={(e) => setCopies(Math.min(Math.max(parseInt(e.target.value, 10) || 1, 1), 10))}
                inputProps={{ min: 1, max: 10 }}
                sx={{ width: 120 }}
              />
              <FormControlLabel
                control={<Checkbox checked={duplex} onChange={(e) => setDuplex(e.target.checked)} />}
                label="Two-sided (duplex)"
              />
              {proposals.length > 1 && (
                <Box>
                  <FormControlLabel
                    sx={{ mb: 0.5 }}
                    control={
                      <Checkbox
                        checked={printIds.size === proposals.length}
                        indeterminate={printIds.size > 0 && printIds.size < proposals.length}
                        onChange={(e) => setPrintIds(e.target.checked ? new Set(proposals.map(p => p.id)) : new Set())}
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Contracts ({printIds.size} of {proposals.length})
                    </Typography>}
                  />
                  <Box sx={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, px: 1, py: 0.5 }}>
                    {proposals.map((p) => (
                      <FormControlLabel
                        key={p.id}
                        sx={{ display: 'flex', mr: 0, '& .MuiFormControlLabel-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                        control={
                          <Checkbox size="small" checked={printIds.has(p.id)} onChange={() => togglePrintId(p.id)} />
                        }
                        label={<Typography variant="body2" noWrap>
                          #{p.publicId}{getClientName(p) ? ` — ${getClientName(p)}` : ''}
                        </Typography>}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">
                The document is rendered on the server and sent to the selected printer
                ({printIds.size} of {proposals.length} proposal{proposals.length !== 1 ? 's' : ''}).
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServerPrintOpen(false)} disabled={serverPrinting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleServerPrint}
            disabled={serverPrinting || !selectedPrinter || serverPrinters.length === 0 || printIds.size === 0}
            startIcon={serverPrinting ? <CircularProgress size={16} color="inherit" /> : <LocalPrintshopIcon />}
          >
            {serverPrinting ? 'Printing…' : 'Print'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document body */}
      <div className="bp-container">
        {proposals.map((proposal, index) => {
          const totalPay = calculateTotalPay(proposal);
          const isFirst = index === 0;
          const primaryClient = proposal.selectedClientId
            ? proposal.property?.clients?.find(c => c.client?.id === proposal.selectedClientId)?.client
            : proposal.property?.clients?.[0]?.client;
          const { schedule, dayInfo } = getInstallInfo(proposal);
          const equipment = getEquipment(proposal);
          const supplies = getSupplies(proposal);
          const scopeItems = getScopeItems(proposal);
          const overview = getJobOverview(proposal);
          const installerNotes = proposal.notesForInstallers
            ?.filter(n => n?.note?.trim())
            .map(n => n.note) || [];
          const workDescriptions = proposal.proposalType === 'Service'
            ? (proposal.workDescriptions || []) : [];
          const airflows = (proposal.proposalType !== 'Service' && proposal.proposalAirflows?.length > 0)
            ? [...proposal.proposalAirflows].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
            : [];

          // Once LLM-generated work instructions exist, drop the source sections
          // they were distilled from (configurable on the AI settings page).
          const hasInstructions = !!proposal.foremanInstructions;
          const isHidden = (key) => hasInstructions && hideSections.includes(key);
          const hideEquipment = isHidden('equipment');
          const hideSupplies = isHidden('supplies');
          const hideWorkDesc = isHidden('workDescription');
          const hideAirflow = isHidden('airflow');
          const hideScope = isHidden('scope');

          return (
            <Fragment key={proposal.id}>
            <div className="no-print" style={{ maxWidth: '190mm', margin: '8px auto 0', display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" variant="outlined" onClick={() => handleRegenInstructions(proposal.id)} disabled={regenId === proposal.id}>
                {regenId === proposal.id ? 'Generating…' : 'Regenerate instructions'}
              </Button>
            </div>
            <div ref={el => { proposalRefs.current[proposal.id] = el; }} className={`proposal-print-paper${isFirst ? ' first-proposal' : ''}`}>

              {/* ---- HEADER ---- */}
              <div className="bp-header">
                <div>
                  <div>
                    <span className="bp-contract-id">#{proposal.publicId}</span>
                    {schedule.length > 0 && (
                      <span className="bp-date">
                        {' \u2014 '}
                        {schedule.map((s, i) => (
                          <span key={s.id || i}>
                            {moment(s.date).utc().format('MM/DD/YYYY')}
                            {s.installationTime && ` ${s.installationTime}`}
                            {dayInfo && ` (Day ${dayInfo.day}/${dayInfo.total})`}
                            {i < schedule.length - 1 && ', '}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                  <div className="bp-issue-date">Issue Date: {moment(proposal.createdAt).format('MM/DD/YYYY')}</div>
                </div>
                <div className="bp-tech">
                  {proposal.createdBy?.fullName || 'Airmaxx'}
                  {proposal.createdBy?.phoneNumber &&
                    ` \u00B7 ${formatPhone(proposal.createdBy.phoneNumber)}`}
                </div>
              </div>

              {/* ---- CUSTOMER ---- */}
              <div className="bp-customer">
                <strong>{primaryClient?.fullName || 'Customer'}</strong>
                <span className="bp-sep">{'·'}</span>
                {proposal.property?.address || 'No address'}
                {primaryClient?.phoneNumber && (
                  <><span className="bp-sep">{'·'}</span>{formatPhone(primaryClient.phoneNumber)}</>
                )}
              </div>
              {proposal.billingAddress && (
                <div className="bp-billing">Billing: {proposal.billingAddress}</div>
              )}

              {/* ---- INSTALLER NOTES ---- */}
              {installerNotes.length > 0 && (
                <div className="bp-notes">
                  <span className="bp-notes-label">NOTES:</span>
                  {installerNotes.map((note, i) => (
                    <span key={i} className="bp-notes-text">
                      {i > 0 && ' | '}{note}
                    </span>
                  ))}
                </div>
              )}

              {/* ---- EQUIPMENT ---- */}
              {equipment.length > 0 && !hideEquipment && (
                <div className="bp-section">
                  <div className="bp-section-title">Equipment</div>
                  <table className="bp-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Manufacturer</th>
                        <th>Model</th>
                        <th>S/N</th>
                      </tr>
                    </thead>
                    <tbody>
                      {equipment.map((eq, i) => (
                        <tr key={i}>
                          <td>{eq.type}</td>
                          <td>{eq.manufacturer}</td>
                          <td><strong>{eq.model}</strong></td>
                          <td className={eq.serial ? 'bp-sn' : 'bp-sn-empty'}>
                            {eq.serial || '\u2014'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ---- SUPPLIES ---- */}
              {supplies.length > 0 && !hideSupplies && (
                <div className="bp-section">
                  <div className="bp-section-title">Supplies</div>
                  <table className="bp-table bp-table-compact">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th style={{ width: 50, textAlign: 'center' }}>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplies.map((s, i) => (
                        <tr key={i}>
                          <td>{s.name}</td>
                          <td style={{ textAlign: 'center' }}>{s.qty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ---- WORK INSTRUCTIONS (LLM) ---- */}
              {(proposal.foremanInstructions || overview) && (
                <div className="bp-section">
                  <div className="bp-section-title">Work Instructions</div>
                  <div className="bp-pre">
                    {proposal.foremanInstructions
                      ? proposal.foremanInstructions
                      : stripHtml(overview)}
                  </div>
                </div>
              )}

              {/* ---- WORK DESCRIPTION (Service proposals) ---- */}
              {workDescriptions.length > 0 && !hideWorkDesc && (
                <div className="bp-section">
                  <div className="bp-section-title">Work Description</div>
                  <ul className="bp-list">
                    {workDescriptions.map((wd, i) => (
                      <li key={wd.id || i}>{wd.description}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ---- AIRFLOW ---- */}
              {airflows.length > 0 && !hideAirflow && (
                <div className="bp-section">
                  <div className="bp-section-title">Airflow</div>
                  <table className="bp-table">
                    <thead>
                      <tr>
                        <th style={{ width: '30%' }}>Name</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {airflows.map((af, i) => (
                        <tr key={af.id || i}>
                          <td><strong>{af.name}</strong></td>
                          <td>{af.description || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ---- SCOPE OF WORK ---- */}
              {scopeItems.length > 0 && !hideScope && (
                <div className="bp-section">
                  <div className="bp-section-title">Scope of Work</div>
                  <ul className="bp-list">
                    {scopeItems.map((item, i) => <li key={i}>{stripHtml(item)}</li>)}
                  </ul>
                </div>
              )}

              {/* ---- TOTAL TO PAY ---- */}
              <div className="bp-total">
                <span className="bp-total-company">AIRMAXX INC. 1865 John Towers Ave. El Cajon, 92020</span>
                <span className="bp-total-amount">
                  TOTAL TO PAY: ${totalPay.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
                  {proposal.paymentNotes && (
                <div className="bp-payment-notes">{proposal.paymentNotes}</div>
              )}

              {/* ---- SIGNATURE BLOCK ---- */}
              <div className="bp-signature">
                <div className="bp-sig-row">
                  <span className="bp-sig-fixed">Date _____/_____/_____</span>
                  <span className="bp-sig-fixed">work performed by Foreman</span>
                  <span className="bp-line" />
                </div>
                <div className="bp-sig-row">
                  <span className="bp-sig-fixed">Helpers:</span>
                  <span className="bp-line" />
                </div>
                <div className="bp-sig-row">
                  <span className="bp-line" />
                  <span className="bp-sig-fixed">From</span>
                  <span className="bp-line-short" />
                  <span className="bp-sig-fixed">To</span>
                  <span className="bp-line-short" />
                </div>
                <div className="bp-sig-row">
                  <span className="bp-sig-fixed">Foreman Signature</span>
                  <span className="bp-line" />
                </div>
              </div>

            </div>

              {/* Blank page for duplex when proposal has odd page count */}
              {oddPageIds.has(proposal.id) && (
                <div className="bp-duplex-blank">
                  <span className="no-print">{'\u2190'} Blank for duplex (after {proposal.publicId}) {'\u2192'}</span>
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
    </Fragment>
  );
};

export default PrintBatch;
